import { DEFAULT_LOCALE, type Locale } from "./constants.js";
import enCA from "./locales/en-CA/sensorNaming.json" with { type: "json" };
import enUS from "./locales/en-US/sensorNaming.json" with { type: "json" };
import esMX from "./locales/es-MX/sensorNaming.json" with { type: "json" };
import frCA from "./locales/fr-CA/sensorNaming.json" with { type: "json" };
import jaJP from "./locales/ja-JP/sensorNaming.json" with { type: "json" };
import paIN from "./locales/pa-IN/sensorNaming.json" with { type: "json" };
import ptPT from "./locales/pt-PT/sensorNaming.json" with { type: "json" };
import { getClusterElementNameKey, getSensorElementNameKey, getUnitCategoryNameKey } from "./sensorNamingKeys.js";

// A single display-name entry. Deliberately an object rather than a bare string so per-name
// extras (e.g. a future `shortName`) can be added without a catalog migration.
export type SensorNamingNameEntry = {
    name: string;
    shortName?: string;
};

/** Localized name overrides keyed by a numeric `SensorElementOutput.subChannel`. */
type OverridesBySubChannel = {
    [subChannel: number]: SensorNamingNameEntry;
};

/** Per-sensor-element overrides within one sensor cluster. */
type OverridesBySensorElement = {
    [sensorElementConfigKey: string]: OverridesBySubChannel;
};

/** All sensor-element name overrides, grouped by sensor cluster. */
type OverridesBySensorCluster = {
    [sensorClusterConfigKey: string]: OverridesBySensorElement;
};

/** Per-locale catalog of localized display names. */
export type SensorNamingCatalog = {
    /**
     * Localized unit-category names, keyed by unit-category config key. Must be complete.
     *
     * @example
     * {
     *   liquidFlow: { name: "Flow Rate" },
     *   stemRadiusChange: { name: "Stem Radius Change" },
     * }
     */
    unitCategories: Record<string, SensorNamingNameEntry>;

    /**
     * Localized common sensor-element names, keyed by sensor-element config key. Must be complete.
     *
     * @example
     * {
     *   flowmeter: { name: "Flow Rate" },
     *   stemRadiusChange: { name: "Stem Radius Change" },
     * }
     */
    sensorElements: Record<string, SensorNamingNameEntry>;

    /**
     * Sparse cluster-specific element-name overrides:
     *
     * cluster config key
     *   -> sensor element config key
     *      -> numeric subchannel
     *         -> localized name
     *
     * @example
     * {
     *   genericFlowmeter: {
     *     flowmeter: {
     *       0: { name: "Flow Rate" },
     *     },
     *   },
     *   EPlantTreeTag_Battery: {
     *     digitalVoltage: {
     *       0: { name: "Battery Voltage" },
     *     },
     *   },
     * }
     */
    sensorCluster: OverridesBySensorCluster;
};

export const sensorNamingCatalogs: Record<Locale, SensorNamingCatalog> = {
    "en-CA": enCA as SensorNamingCatalog,
    "en-US": enUS as SensorNamingCatalog,
    "fr-CA": frCA as SensorNamingCatalog,
    "es-MX": esMX as SensorNamingCatalog,
    "ja-JP": jaJP as SensorNamingCatalog,
    "pt-PT": ptPT as SensorNamingCatalog,
    "pa-IN": paIN as SensorNamingCatalog,
};

/**
 * Resolve a dot-separated key (e.g. `sensorElements.stemRadiusChange.name`) against a
 * sensor-naming catalog and return the leaf string when present.
 * Use this for validation/tooling paths that need a direct catalog lookup.
 */
export function lookupSensorNamingCatalogString(catalog: SensorNamingCatalog, key: string): string | undefined {
    const leaf = key.split(".").reduce<unknown>((cursor, segment) => {
        if (cursor === null || typeof cursor !== "object") return undefined;
        return (cursor as Record<string, unknown>)[segment];
    }, catalog);
    return typeof leaf === "string" ? leaf : undefined;
}

// Resolve a key in `locale`, falling back to DEFAULT_LOCALE when the locale hasn't translated it.
function resolveName(locale: Locale, key: string): string | undefined {
    return (
        lookupSensorNamingCatalogString(sensorNamingCatalogs[locale], key) ??
        lookupSensorNamingCatalogString(sensorNamingCatalogs[DEFAULT_LOCALE], key)
    );
}

// Cluster-specific override of an element's name, or `null` when none is declared for this
// (cluster, element, sub-channel). Internal — callers reach it through `getSensorElementName`,
// which falls back to the common name so consumers always get a printable string.
function lookupClusterElementNameOverride(
    sensorClusterConfigKey: string,
    sensorElementConfigKey: string,
    subChannel: number,
    locale: Locale,
): string | null {
    return (
        resolveName(locale, getClusterElementNameKey(sensorClusterConfigKey, sensorElementConfigKey, subChannel)) ??
        null
    );
}

/**
 * Localized display name for a unit category (e.g. `"Temperature"`). Falls back to the raw
 * `unitCategory` key if the catalog has no entry, so it never throws.
 */
export function getUnitCategoryName(unitCategory: string, locale: Locale = DEFAULT_LOCALE): string {
    return resolveName(locale, getUnitCategoryNameKey(unitCategory)) ?? unitCategory;
}

/**
 * The common, cluster-independent display name for a sensor element (e.g. `"Stem Radius
 * Change"`). Use this only when you have no cluster context — it cannot apply a cluster-specific
 * override. Prefer {@link getSensorElementName} when the cluster is known. Falls back to the raw
 * key if the catalog has no entry.
 */
export function getSensorElementNameInUnknownCluster(
    sensorElementConfigKey: string,
    locale: Locale = DEFAULT_LOCALE,
): string {
    return resolveName(locale, getSensorElementNameKey(sensorElementConfigKey)) ?? sensorElementConfigKey;
}

/**
 * The canonically correct display name for a sensor element given its full cluster context:
 * the cluster-specific override when one is declared, otherwise the common name. Never null —
 * always the best available name. This is the function callers should prefer.
 *
 * `subChannel` disambiguates elements that share `sensorElementConfigKey` within one cluster
 * (see `SensorElementOutput.subChannel`).
 */
export function getSensorElementName(
    sensorClusterConfigKey: string,
    sensorElementConfigKey: string,
    subChannel: number,
    locale: Locale = DEFAULT_LOCALE,
): string {
    return (
        lookupClusterElementNameOverride(sensorClusterConfigKey, sensorElementConfigKey, subChannel, locale) ??
        getSensorElementNameInUnknownCluster(sensorElementConfigKey, locale)
    );
}
